import React, { useEffect, useState, useCallback } from 'react'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { fetchIngredients } from '../../store/ingredientsSlice'
import useCsrfToken from '../../hooks/useCsrfToken'
import { formatPrice } from '../../store/pricesSlice'

interface Store {
  id: number
  name: string
  api_provider: string
}

interface StoreProduct {
  id: number
  store_id: number
  store_name: string
  food_id: number | null
  food_name: string | null
  external_product_id: string
  upc: string | null
  name: string
  brand: string | null
  size: string | null
  category: string | null
  image_url: string | null
  last_synced_at: string | null
  current_price_cents: number | null
  on_sale: boolean
}

interface Pagination {
  page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface PricePoint {
  date: string
  price_cents: number
  sale_price_cents: number | null
  on_sale: boolean
}

const AdminProducts: React.FC = () => {
  const csrfToken = useCsrfToken()
  const dispatch = useAppDispatch()
  const foods = useAppSelector((s) => s.ingredients.items)

  const [stores, setStores] = useState<Store[]>([])
  const [selectedStoreId, setSelectedStoreId] = useState<string>('')
  const [products, setProducts] = useState<StoreProduct[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [unlinkedOnly, setUnlinkedOnly] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [priceHistory, setPriceHistory] = useState<Record<number, PricePoint[]>>({})
  const [linkValues, setLinkValues] = useState<Record<number, string>>({})
  const [saving, setSaving] = useState<Record<number, boolean>>({})

  useEffect(() => {
    dispatch(fetchIngredients({ page: 1, perPage: 500, query: '' }))
    fetch('/api/admin/stores', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => setStores(data.stores || []))
  }, [dispatch])

  const loadProducts = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), per_page: '50' })
    if (query) params.set('q', query)
    if (unlinkedOnly) params.set('linked', 'false')
    if (selectedStoreId) params.set('store_id', selectedStoreId)

    fetch(`/api/admin/store_products?${params}`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        setProducts(data.store_products)
        setPagination(data.pagination)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [page, query, unlinkedOnly, selectedStoreId])

  useEffect(() => { loadProducts() }, [loadProducts])

  const loadPriceHistory = async (productId: number) => {
    if (priceHistory[productId]) return
    const res = await fetch(`/api/admin/store_products/${productId}/price_history`, {
      headers: { Accept: 'application/json' },
    })
    const data = await res.json()
    setPriceHistory((prev) => ({ ...prev, [productId]: data.price_history }))
  }

  const handleExpand = (productId: number) => {
    if (expanded === productId) {
      setExpanded(null)
    } else {
      setExpanded(productId)
      loadPriceHistory(productId)
    }
  }

  const handleLinkSave = async (product: StoreProduct) => {
    setSaving((prev) => ({ ...prev, [product.id]: true }))
    const inputVal = linkValues[product.id] ?? product.food_name ?? ''
    const matched = foods.find((f) => f.name.toLowerCase() === inputVal.toLowerCase())
    const food_id = matched ? matched.id : null

    const res = await fetch(`/api/admin/store_products/${product.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ food_id }),
    })
    const data = await res.json()
    setSaving((prev) => ({ ...prev, [product.id]: false }))
    if (res.ok) {
      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id
            ? { ...p, food_id: data.store_product.food_id, food_name: data.store_product.food_name }
            : p
        )
      )
    }
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <select
          value={selectedStoreId}
          onChange={(e) => { setSelectedStoreId(e.target.value); setPage(1) }}
          className="px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white focus:outline-none focus:border-neon transition-colors text-sm"
        >
          <option value="">All stores</option>
          {stores.map((s) => (
            <option key={s.id} value={String(s.id)}>{s.name}</option>
          ))}
        </select>
        <input
          type="search"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search products…"
          className="flex-1 px-3 py-2 bg-army-950 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-gray-400 whitespace-nowrap cursor-pointer">
          <input
            type="checkbox"
            checked={unlinkedOnly}
            onChange={(e) => { setUnlinkedOnly(e.target.checked); setPage(1) }}
            className="accent-neon"
          />
          Unlinked only
        </label>
      </div>

      {pagination && (
        <p className="text-xs text-gray-500">
          {pagination.total_count.toLocaleString()} products
          {query ? ` matching "${query}"` : ''}
          {unlinkedOnly ? ' (unlinked only)' : ''}
        </p>
      )}

      <div className="bg-navy-900 border border-army-800 rounded-lg overflow-hidden">
        {loading ? (
          <p className="px-4 py-6 text-gray-500 text-sm">Loading…</p>
        ) : products.length === 0 ? (
          <p className="px-4 py-6 text-gray-500 text-sm">No products found.</p>
        ) : (
          <ul className="divide-y divide-army-800">
            {products.map((product) => (
              <li key={product.id}>
                <div
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-army-900 transition-colors"
                  onClick={() => handleExpand(product.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm text-stone-200 font-medium">{product.name}</span>
                      {product.brand && (
                        <span className="text-xs text-gray-500">{product.brand}</span>
                      )}
                      {product.size && (
                        <span className="text-xs text-gray-500">{product.size}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-600">{product.store_name}</span>
                      {product.category && (
                        <span className="text-xs text-gray-600">{product.category}</span>
                      )}
                      {product.food_name ? (
                        <span className="text-xs text-neon">→ {product.food_name}</span>
                      ) : (
                        <span className="text-xs text-gray-700">unlinked</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {product.current_price_cents != null && (
                      <span className={`text-sm font-medium ${product.on_sale ? 'text-neon' : 'text-stone-200'}`}>
                        {formatPrice(product.current_price_cents)}
                        {product.on_sale && <span className="text-xs text-gray-500 ml-1">sale</span>}
                      </span>
                    )}
                  </div>
                </div>

                {expanded === product.id && (
                  <div className="px-4 pb-4 bg-army-950 border-t border-army-800 space-y-3">
                    <div className="flex gap-2 items-end pt-3">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 block mb-1">Link to food ingredient</label>
                        <input
                          type="text"
                          list={`food-list-${product.id}`}
                          value={linkValues[product.id] ?? product.food_name ?? ''}
                          onChange={(e) =>
                            setLinkValues((prev) => ({ ...prev, [product.id]: e.target.value }))
                          }
                          placeholder="Type a food name…"
                          className="w-full px-3 py-2 bg-army-900 border border-army-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-neon transition-colors text-sm"
                        />
                        <datalist id={`food-list-${product.id}`}>
                          {foods.map((f) => (
                            <option key={f.id} value={f.name} />
                          ))}
                        </datalist>
                      </div>
                      <button
                        onClick={() => handleLinkSave(product)}
                        disabled={saving[product.id]}
                        className="px-3 py-2 bg-neon text-army-950 font-semibold rounded-lg text-sm hover:opacity-90 transition-opacity disabled:opacity-50 shrink-0"
                      >
                        {saving[product.id] ? 'Saving…' : 'Save link'}
                      </button>
                      {product.food_id && (
                        <button
                          onClick={() => {
                            setLinkValues((prev) => ({ ...prev, [product.id]: '' }))
                            handleLinkSave({ ...product, food_name: '' })
                          }}
                          className="px-3 py-2 text-xs text-gray-500 hover:text-rose-400 transition-colors"
                        >
                          Unlink
                        </button>
                      )}
                    </div>

                    {priceHistory[product.id] !== undefined && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Price history (last 30 days)</p>
                        {priceHistory[product.id].length === 0 ? (
                          <p className="text-xs text-gray-600">No price data recorded yet.</p>
                        ) : (
                          <div className="flex items-end gap-px h-12" title="Price history sparkline">
                            {(() => {
                              const history = priceHistory[product.id]
                              const prices = history.map((r) =>
                                r.on_sale && r.sale_price_cents != null ? r.sale_price_cents : r.price_cents
                              )
                              const min = Math.min(...prices)
                              const max = Math.max(...prices)
                              const range = max - min || 1
                              return history.map((r, i) => {
                                const val = r.on_sale && r.sale_price_cents != null ? r.sale_price_cents : r.price_cents
                                const heightPct = ((val - min) / range) * 100
                                return (
                                  <div
                                    key={i}
                                    title={`${r.date}: ${formatPrice(val)}${r.on_sale ? ' (sale)' : ''}`}
                                    className={`flex-1 rounded-sm ${r.on_sale ? 'bg-neon' : 'bg-army-700'}`}
                                    style={{ height: `${Math.max(15, heightPct)}%` }}
                                  />
                                )
                              })
                            })()}
                          </div>
                        )}
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-600">
                            {priceHistory[product.id][0]?.date}
                          </span>
                          <span className="text-xs text-gray-600">
                            {priceHistory[product.id][priceHistory[product.id].length - 1]?.date}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {pagination && pagination.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm bg-army-800 text-gray-300 rounded-lg hover:bg-army-700 disabled:opacity-40 transition-colors"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-500">
            {page} / {pagination.total_pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            disabled={page === pagination.total_pages}
            className="px-3 py-1.5 text-sm bg-army-800 text-gray-300 rounded-lg hover:bg-army-700 disabled:opacity-40 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

export default AdminProducts
